import { useAugmentationPanelHashNavigation } from "@pathwright/ux/src/components/augmentation/hashNavigation"
import { AUGMENTATION_SECTION_FOCUS_CLASS } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import { RefObject, useCallback, useEffect, useRef } from "react"
import {
  COVER_PAGE_HASH_RETRY,
  COVER_PAGE_HIGHLIGHT_DURATION_MS,
  COVER_PAGE_SCROLL_EMPHASIS,
  COVER_PAGE_SECTION_SCROLL_OFFSET
} from "./constants"
import { parseCoverPageScrollHash } from "./design/utils"
import {
  CoverPageHashSectionKey,
  CoverPagePanelSectionKey,
  toCoverPageScrollHashKey
} from "./types"

const highlightTimeouts = new Map<string, number>()

export function getCoverPagePanelSectionElementId(
  scrollHashKey: CoverPageHashSectionKey
) {
  return `cover-panel-section-${scrollHashKey}`
}

/**
 * Briefly apply the section focus/active state by toggling
 * {@link AUGMENTATION_SECTION_FOCUS_CLASS}. This module only says which section
 * is focused; the Cover Page surface and the panel each own what that looks
 * like (see `AUGMENTATION_SECTION_FOCUS_TARGET_CSS`).
 */
function applyHighlight(element: HTMLElement, timeoutKey: string) {
  const existingTimeout = highlightTimeouts.get(timeoutKey)
  if (existingTimeout !== undefined) {
    window.clearTimeout(existingTimeout)
    highlightTimeouts.delete(timeoutKey)
  }

  element.classList.remove(AUGMENTATION_SECTION_FOCUS_CLASS)
  void element.offsetHeight
  element.classList.add(AUGMENTATION_SECTION_FOCUS_CLASS)

  const timeoutId = window.setTimeout(() => {
    element.classList.remove(AUGMENTATION_SECTION_FOCUS_CLASS)
    highlightTimeouts.delete(timeoutKey)
  }, COVER_PAGE_HIGHLIGHT_DURATION_MS)
  highlightTimeouts.set(timeoutKey, timeoutId)
}

function highlightElementById(elementId: string) {
  const element = document.getElementById(elementId)
  if (!element) {
    return false
  }

  applyHighlight(element, elementId)
  return true
}

function retryUntilReady(
  attempt: () => boolean,
  { maxAttempts, intervalMs } = COVER_PAGE_HASH_RETRY
) {
  if (attempt()) {
    return
  }

  let attempts = 1

  const tryAgain = () => {
    if (attempt()) {
      return
    }

    attempts += 1
    if (attempts < maxAttempts) {
      window.setTimeout(tryAgain, intervalMs)
    }
  }

  window.setTimeout(tryAgain, intervalMs)
}

/** Highlight main-page and/or panel section targets for a cover page section. */
export function highlightCoverPageSection(
  sectionKey: CoverPagePanelSectionKey | CoverPageHashSectionKey,
  {
    // "main" refers to the main Cover Page (as opposed to the side panel)
    highlightMain = true,
    highlightPanel = true
  } = {}
) {
  const scrollHashKey = toCoverPageScrollHashKey(
    sectionKey as CoverPagePanelSectionKey
  )

  if (highlightMain) {
    retryUntilReady(() => highlightElementById(scrollHashKey))
  }

  if (highlightPanel) {
    retryUntilReady(() =>
      highlightElementById(getCoverPagePanelSectionElementId(scrollHashKey))
    )
  }
}

/** Scroll to a section, returning the target scroll position (null if absent). */
function scrollToSection(
  sectionKey: CoverPageHashSectionKey,
  scrollContainer: HTMLElement,
  offset = COVER_PAGE_SECTION_SCROLL_OFFSET
) {
  const element = document.getElementById(sectionKey)
  if (!element) {
    return null
  }

  const targetPosition = Math.max(
    0,
    scrollContainer.scrollTop +
      (element.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top) -
      offset
  )

  scrollContainer.scrollTo({ top: targetPosition, behavior: "smooth" })
  return targetPosition
}

/**
 * Run once the smooth scroll is most of the way to `targetTop`, so the emphasis
 * fades in as the section arrives instead of at the moment of the click, where
 * the motion masks it.
 *
 * Tracks position rather than elapsed time or scroll events: a smooth scroll can
 * take a beat to start, so anything clock-based fires while the page is still
 * stationary. The target is re-clamped each frame so a section near the bottom
 * (which can't scroll all the way up to the offset) still reads as complete.
 */
function whenScrollMostlyComplete(
  scrollContainer: HTMLElement,
  targetTop: number,
  onReady: () => void
) {
  const startedAt = performance.now()
  const startTop = scrollContainer.scrollTop

  const check = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer
    const reachable = Math.min(
      targetTop,
      Math.max(0, scrollHeight - clientHeight)
    )
    const distance = Math.abs(reachable - startTop)
    const progress =
      distance <= 1 ? 1 : Math.abs(scrollTop - startTop) / distance

    if (
      progress >= COVER_PAGE_SCROLL_EMPHASIS.progress ||
      performance.now() - startedAt >= COVER_PAGE_SCROLL_EMPHASIS.maxWaitMs
    ) {
      onReady()
      return
    }

    requestAnimationFrame(check)
  }

  requestAnimationFrame(check)
}

/** Scroll the cover page to a hash target and highlight matching sections. */
export function navigateToCoverPageHashSection(
  hash: string | null | undefined,
  scrollContainer: HTMLElement | null,
  offset = COVER_PAGE_SECTION_SCROLL_OFFSET
) {
  const sectionKey = parseCoverPageScrollHash(hash)
  if (!sectionKey) {
    return
  }

  if (!scrollContainer) {
    highlightCoverPageSection(sectionKey)
    return
  }

  // The panel doesn't scroll, so it can emphasize right away. The main page
  // waits until the scroll is nearly on the section.
  highlightCoverPageSection(sectionKey, { highlightMain: false })

  retryUntilReady(() => {
    const targetTop = scrollToSection(sectionKey, scrollContainer, offset)
    if (targetTop === null) {
      return false
    }

    whenScrollMostlyComplete(scrollContainer, targetTop, () =>
      highlightCoverPageSection(sectionKey, { highlightPanel: false })
    )
    return true
  })
}

type UseCoverPageHashScrollArgs = {
  scrollContainerRef: RefObject<HTMLDivElement>
  /**
   * Changes when async/conditional sections mount (e.g. CoverPageMore after
   * related-resources load). Re-runs hash scroll so targets exist on first visit.
   */
  sectionMountKey?: string
}

export function useCoverPageHashScroll({
  scrollContainerRef,
  sectionMountKey
}: UseCoverPageHashScrollArgs) {
  const navigate = useCallback(
    (hash?: string | null) => {
      navigateToCoverPageHashSection(hash, scrollContainerRef.current)
    },
    [scrollContainerRef]
  )

  useAugmentationPanelHashNavigation(navigate)

  const sectionMountKeyRef = useRef(sectionMountKey)
  useEffect(() => {
    if (sectionMountKeyRef.current === sectionMountKey) {
      return
    }
    sectionMountKeyRef.current = sectionMountKey
    navigate(window.location.hash)
  }, [navigate, sectionMountKey])

  return { scrollToSection: navigate }
}
